import type { JobSummary } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { AiStarCallout } from '../components/AiStarCallout.js';
import { AttritionChart } from '../components/AttritionChart.js';
import { BillingPrompt } from '../components/BillingPrompt.js';
import { CommissionAtRiskChart } from '../components/CommissionAtRiskChart.js';
import { DataQualityCard } from '../components/DataQualityCard.js';
import { JobStatusBadge } from '../components/JobStatusBadge.js';
import { OnboardingChecklist } from '../components/OnboardingChecklist.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { RoiStrip } from '../components/RoiStrip.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import {
  Card,
  EstimateBanner,
  EstimatedBadge,
  Field,
  ICONS,
  Icon,
  Spinner,
  StatPill,
  inputClass,
} from '../components/ui.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

const num = (n: number) => n.toLocaleString('en-US');

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

interface Totals {
  memberCount: number;
  green: number;
  yellow: number;
  red: number;
  new: number;
  reappeared: number;
  commissionAtRisk: number;
  commissionDropped: number;
  commissionReduced: number;
  reducedCount: number;
  commissionOwed: number;
  owedEvaluated: number;
  owedTotal: number;
  /** Owed is (partly) built on an auto-seeded estimate rate — show "estimated". */
  owedEstimated?: boolean;
  chargebackCount: number;
  chargebackAmount: number;
}

type Tone = 'emerald' | 'amber' | 'rose';

// Literal class strings per tone (Tailwind can't see dynamically-built names).
const TONE: Record<
  Tone,
  { card: string; chip: string; label: string; value: string; primary: string; secondary: string }
> = {
  emerald: {
    card: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20',
    chip: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    label: 'text-emerald-700 dark:text-emerald-300',
    value: 'text-emerald-700 dark:text-emerald-300',
    primary: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700',
    secondary:
      'border border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40',
  },
  amber: {
    card: 'border-amber-200 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/20',
    chip: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    label: 'text-amber-700 dark:text-amber-300',
    value: 'text-amber-700 dark:text-amber-300',
    primary: 'bg-amber-600 text-white shadow-sm hover:bg-amber-700',
    secondary:
      'border border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-950/40',
  },
  rose: {
    card: 'border-rose-200 bg-rose-50/60 dark:border-rose-900/40 dark:bg-rose-950/20',
    chip: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
    label: 'text-rose-700 dark:text-rose-300',
    value: 'text-rose-700 dark:text-rose-300',
    primary: 'bg-rose-600 text-white shadow-sm hover:bg-rose-700',
    secondary:
      'border border-rose-300 text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/40',
  },
};

interface Cta {
  label: string;
  to: string;
  primary?: boolean;
}

/** One of the three headline status cards (owed / chargebacks / at-risk). All
 * three share this layout so they're identical in size + feel; CTAs pin to the
 * bottom so cards stay even-height in the grid. */
function HeadlineCard({
  tone,
  icon,
  label,
  value,
  sub,
  ctas,
  loading = false,
  badge,
}: {
  tone: Tone;
  icon: string;
  label: string;
  value: string;
  sub: string;
  ctas: Cta[];
  loading?: boolean;
  badge?: ReactNode;
}) {
  const t = TONE[tone];
  return (
    <Card className={`flex h-full flex-col ${t.card}`}>
      <div className="flex items-center gap-2.5">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.chip}`}
        >
          <Icon path={icon} size={22} />
        </span>
        <span className={`text-sm font-medium ${t.label}`}>{label}</span>
      </div>
      <div className={`mt-3 flex items-center gap-2 text-3xl font-bold tracking-tight ${t.value}`}>
        {loading ? <Spinner className="my-1 h-7 w-7 opacity-70" /> : value}
        {!loading && badge}
      </div>
      <p className="mt-1.5 text-xs text-neutral-600 dark:text-neutral-400">{sub}</p>
      {ctas.length > 0 && (
        <div className="mt-auto flex flex-wrap gap-2 pt-4">
          {ctas.map((c) => (
            <Link
              key={c.label}
              to={c.to}
              className={`rounded-lg px-3.5 py-2 text-sm font-medium transition ${c.primary ? t.primary : t.secondary}`}
            >
              {c.label}
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

export function Dashboard() {
  const { client, baseUrl, token } = useAuth();
  // Empty until we discover the latest period that has data (filled on load).
  const [period, setPeriod] = useState('');
  const [carrierId, setCarrierId] = useState(''); // '' = all carriers
  const [workspaceId, setWorkspaceId] = useState(''); // '' = all workspaces
  const [totals, setTotals] = useState<Totals | null>(null);
  // Stored census size for the current carrier/workspace filter — the FULL book,
  // so the Members tile can show it next to paid-this-period. `policies` is the
  // roster row count (policy-keyed); `members` is distinct PEOPLE when the roster
  // carries person ids (one person often holds several policies), else null.
  const [book, setBook] = useState<{ policies: number; members: number | null }>({
    policies: 0,
    members: null,
  });
  const [attrition, setAttrition] = useState<number | null>(null);
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const { carriers } = useCarriers();
  const carrierNames = useMemo(
    () => Object.fromEntries(carriers.map((c) => [c.id, c.name])),
    [carriers],
  );
  const [error, setError] = useState<string | null>(null);
  // True while a rollup/attrition/jobs fetch is in flight — drives the spinners on
  // the initial load AND every filter (carrier/period/workspace) change.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setError(null);
    setLoading(true);
    (async () => {
      // No period chosen → ask the API for the latest period with data.
      const r = await client.rollup(
        period || undefined,
        carrierId || undefined,
        workspaceId || undefined,
      );
      if (!active) return;
      setTotals(r.totals);
      const resolved = r.period ?? period ?? currentPeriod();
      // Resolving the latest period re-runs this effect (period dep) — keep the
      // spinner up until the resolved-period pass finishes rather than flipping it
      // off here only to turn it back on.
      if (!period && r.period) {
        setPeriod(r.period);
        return;
      }
      const [a, j] = await Promise.all([
        client.attrition(resolved, carrierId || undefined, workspaceId || undefined),
        client.listJobs({ limit: 5, carrierId: carrierId || undefined }),
      ]);
      if (!active) return;
      setAttrition(a.attritionRate);
      setJobs(j.data);
      setLoading(false);
    })().catch((e) => {
      if (!active) return;
      setError(e instanceof Error ? e.message : 'Failed to load');
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [client, period, carrierId, workspaceId]);

  // Full book size (stored census) for the current filter — independent of period.
  useEffect(() => {
    let active = true;
    const qs = new URLSearchParams();
    if (carrierId) qs.set('carrierId', carrierId);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/census${qs.size ? `?${qs}` : ''}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (!active) return;
        const body = b as { count?: number; members?: number | null } | null;
        setBook({
          policies: Number(body?.count ?? 0),
          members: body?.members != null ? Number(body.members) : null,
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, carrierId, workspaceId]);

  // "Review members" → the current period's results grid, pre-filtered to the
  // at-risk members behind the dollars: dropped (red) + reduced (yellow). Falls
  // back to the most recent job, then the jobs list if there are none yet.
  const periodJob =
    jobs.find((j) => `${j.periodYear}-${String(j.periodMonth).padStart(2, '0')}` === period) ??
    jobs[0];
  const reviewHref = periodJob ? `/jobs/${periodJob.id}?status=red,yellow` : '/jobs';
  const statSpinner = <Spinner className="h-5 w-5 text-neutral-400" />;

  // --- The three headline status cards (green owed / yellow chargebacks / red at risk).
  const greenCtas: Cta[] = [];
  if (totals && totals.commissionOwed > 0 && periodJob) {
    greenCtas.push({ label: 'Review members', to: `/jobs/${periodJob.id}?owed=1`, primary: true });
  }
  greenCtas.push({
    label: totals && totals.owedEvaluated > 0 ? 'Manage rates' : 'Set rates',
    to: '/rates',
  });
  const greenSub =
    totals && totals.owedTotal > 0
      ? `Expected vs actual · ${num(totals.owedEvaluated)}/${num(totals.owedTotal)} policies evaluated${
          totals.owedEstimated
            ? ' · Estimated from your statements — confirm your rate in Manage rates'
            : ''
        }`
      : 'Set your contracted rates to compute what carriers owe you';

  const cbCount = totals?.chargebackCount ?? 0;
  const chargebacksHref = `/chargebacks${(() => {
    const qs = new URLSearchParams();
    if (carrierId) qs.set('carrierId', carrierId);
    if (workspaceId) qs.set('workspaceId', workspaceId);
    return qs.toString() ? `?${qs}` : '';
  })()}`;
  const yellowCtas: Cta[] =
    cbCount > 0 ? [{ label: 'Review chargebacks', to: chargebacksHref }] : [];
  const yellowSub =
    cbCount > 0 ? `${num(cbCount)} policies clawed back` : 'No chargebacks this period';

  const redSub = totals
    ? `${formatCurrency(totals.commissionDropped)} dropped · ${formatCurrency(totals.commissionReduced)} paid less`
    : 'vs the prior period';
  const redCtas: Cta[] = [{ label: 'Review members', to: reviewHref }];

  return (
    <div className="space-y-6">
      <AiStarCallout />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <div className="flex flex-wrap items-end gap-3">
          <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
          <div className="w-48">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
              >
                <option value="">All carriers</option>
                {Object.entries(carrierNames).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="w-48">
            <Field label="Period">
              <PeriodPicker value={period} onChange={setPeriod} placeholder="Latest period" />
            </Field>
          </div>
        </div>
      </div>
      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      <OnboardingChecklist />
      <RoiStrip />

      {totals?.owedEstimated && <EstimateBanner />}

      <BillingPrompt />

      {/* Member status mix + movement — sits above the three headline status cards. */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <div className="flex items-center gap-1.5 text-sm text-neutral-500">
            <Icon path={ICONS.users} size={15} />
            Members paid
          </div>
          <div className="mt-1 text-2xl font-semibold">
            {loading ? statSpinner : totals ? num(totals.memberCount) : '—'}
          </div>
          {/* Paid-this-period is the heartbeat, but a customer who knows their book
              is bigger reads a bare number as "missing members" — anchor it with the
              stored roster, in the right units: members are PEOPLE (when the roster
              carries person ids), policies are what payments land on. */}
          {book.policies > 0 && !loading && (
            <div
              className="mt-0.5 text-xs text-neutral-500"
              title="Your stored roster (census) — everyone on the books, whether or not a payment landed this period. One member often holds several policies; payments (and the count above) are per policy."
            >
              {book.members !== null
                ? `on the books: ${num(book.members)} members · ${num(book.policies)} policies`
                : `of ${num(book.policies)} policies on the books`}
            </div>
          )}
        </Card>
        <Card>
          <div className="mb-2 flex items-center gap-1.5 text-sm text-neutral-500">
            <Icon path={ICONS.pie} size={15} />
            Status mix
          </div>
          {loading ? (
            <div className="mt-1">{statSpinner}</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <StatPill status="green" label={`${num(totals?.green ?? 0)} green`} />
              <StatPill status="yellow" label={`${num(totals?.yellow ?? 0)} yellow`} />
              <StatPill status="red" label={`${num(totals?.red ?? 0)} red`} />
            </div>
          )}
        </Card>
        <Card>
          <div className="flex items-center gap-1.5 text-sm text-neutral-500">
            <Icon path={ICONS.trendDown} size={15} />
            Attrition
          </div>
          <div className="mt-1 text-2xl font-semibold">
            {loading ? statSpinner : attrition === null ? '—' : `${(attrition * 100).toFixed(1)}%`}
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-1.5 text-sm text-neutral-500">
            <Icon path={ICONS.sparkle} size={15} />
            New / Reappeared
          </div>
          <div className="mt-1 text-2xl font-semibold">
            {loading
              ? statSpinner
              : totals
                ? `${num(totals.new)} / ${num(totals.reappeared)}`
                : '—'}
          </div>
        </Card>
      </div>

      {/* The three headline statuses, uniform: green owed · yellow chargebacks · red at risk. */}
      <div className="grid gap-4 md:grid-cols-3">
        <HeadlineCard
          tone="emerald"
          icon={ICONS.dollar}
          label={
            totals?.owedEstimated ? 'Commission owed (estimated)' : 'Commission owed (recoverable)'
          }
          value={totals ? formatCurrency(totals.commissionOwed) : '—'}
          badge={totals?.owedEstimated ? <EstimatedBadge /> : undefined}
          sub={greenSub}
          ctas={greenCtas}
          loading={loading}
        />
        <HeadlineCard
          tone="amber"
          icon={ICONS.undo}
          label="Chargebacks this period"
          value={totals ? formatCurrency(totals.chargebackAmount) : '—'}
          sub={yellowSub}
          ctas={yellowCtas}
          loading={loading}
        />
        <HeadlineCard
          tone="rose"
          icon={ICONS.alert}
          label="Commission at risk this period"
          value={totals ? formatCurrency(totals.commissionAtRisk) : '—'}
          sub={redSub}
          ctas={redCtas}
          loading={loading}
        />
      </div>

      <DataQualityCard period={period} workspaceId={workspaceId || undefined} />

      <CommissionAtRiskChart
        carrierId={carrierId || undefined}
        workspaceId={workspaceId || undefined}
      />

      <AttritionChart carrierId={carrierId || undefined} workspaceId={workspaceId || undefined} />

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">Recent jobs</h2>
          <Link to="/jobs" className="text-sm text-emerald-600 hover:underline">
            View all
          </Link>
        </div>
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {loading && (
            <div className="flex items-center gap-2 py-4 text-sm text-neutral-500">
              <Spinner className="h-4 w-4" /> Loading…
            </div>
          )}
          {!loading && jobs.length === 0 && (
            <p className="py-4 text-sm text-neutral-500">No jobs yet.</p>
          )}
          {jobs.map((j) => (
            <Link
              key={j.id}
              to={`/jobs/${j.id}`}
              className="group flex items-center justify-between gap-3 py-3 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium group-hover:text-emerald-600">
                  {carrierNames[j.carrierId] ?? `${j.carrierId.slice(0, 8)}…`}
                </div>
                <div className="text-xs text-neutral-400">
                  {formatPeriodLong({ year: j.periodYear, month: j.periodMonth })}
                  <span className="ml-2 font-mono">{j.id.slice(0, 8)}</span>
                </div>
              </div>
              <JobStatusBadge status={j.status} />
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
